import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { PanelLeftClose, PanelLeftOpen, PhoneCall, RefreshCw, ShieldCheck, UserRound } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { getEmployeesPresence, pingPresence } from "../services/api";

const roleLabels = {
    superadmin: "Суперадмин",
    default: "Сотрудник",
};

const contactLabels = {
    self: "Это вы",
    confirmed: "Контакт",
    pending: "Запрос",
    none: "Не в контактах",
};

function formatLastSeen(value) {
    if (!value) {
        return "Еще не был в сети";
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return "Последний вход неизвестен";
    }

    return `Был: ${date.toLocaleString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    })}`;
}

const EmployeeRightPanel = ({ isCollapsed = false, onToggleCollapsed }) => {
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();
    const [employees, setEmployees] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    const sortedEmployees = useMemo(() => [...employees].sort((left, right) => {
        if (left.is_self !== right.is_self) {
            return left.is_self ? -1 : 1;
        }
        if (left.is_online !== right.is_online) {
            return left.is_online ? -1 : 1;
        }
        return left.username.localeCompare(right.username);
    }), [employees]);

    const onlineCount = useMemo(
        () => employees.filter((employee) => employee.is_online).length,
        [employees],
    );

    const loadEmployees = useCallback(async () => {
        if (!user) {
            setEmployees([]);
            return;
        }

        setIsLoading(true);
        setError("");
        try {
            await pingPresence();
            const response = await getEmployeesPresence();
            setEmployees(response.data);
        } catch (err) {
            setError(err.response?.data?.detail || "Не удалось загрузить сотрудников.");
        } finally {
            setIsLoading(false);
        }
    }, [user]);

    useEffect(() => {
        loadEmployees();
        if (!user) {
            return undefined;
        }

        const interval = window.setInterval(loadEmployees, 30000);
        return () => window.clearInterval(interval);
    }, [loadEmployees, user]);

    if (!user) {
        return null;
    }

    return (
        <aside className={`employee-panel ${isCollapsed ? "is-collapsed" : ""}`}>
            <div className="employee-panel-header">
                <div>
                    <span className="eyebrow">Команда</span>
                    <h2>Сотрудники</h2>
                    <p>{onlineCount} онлайн из {employees.length}</p>
                </div>
                <div className="employee-panel-tools">
                    <button
                        className="icon-action"
                        onClick={onToggleCollapsed}
                        type="button"
                        aria-expanded={!isCollapsed}
                        title={isCollapsed ? "Показать сотрудников" : "Свернуть сотрудников"}
                    >
                        {isCollapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
                    </button>
                    <button className="icon-action" onClick={loadEmployees} disabled={isLoading} title="Обновить сотрудников">
                        <RefreshCw size={16} />
                    </button>
                </div>
            </div>

            {error && <p className="employee-panel-error">{error}</p>}

            {isCollapsed && (
                <div className="employee-collapsed-list" aria-label="Статусы сотрудников">
                    {sortedEmployees.map((employee) => (
                        <div
                            className={`employee-collapsed-avatar ${employee.is_online ? "is-online" : ""}`}
                            key={employee.id}
                            title={`${employee.username}: ${employee.is_online ? "онлайн" : formatLastSeen(employee.last_seen_at)}`}
                        >
                            <UserRound size={17} />
                        </div>
                    ))}
                </div>
            )}

            <div className="employee-list">
                {sortedEmployees.map((employee) => (
                    <article className="employee-row" key={employee.id}>
                        <div className={`employee-avatar ${employee.is_online ? "is-online" : ""}`}>
                            <UserRound size={18} />
                        </div>
                        <div className="employee-copy">
                            <strong>{employee.username}</strong>
                            <span>{roleLabels[employee.role] || employee.role || "Сотрудник"}</span>
                            <small>{contactLabels[employee.contact_status] || employee.contact_status}</small>
                            <small>{employee.is_online ? "Сейчас в сети" : formatLastSeen(employee.last_seen_at)}</small>
                        </div>
                        <span className={`presence-dot ${employee.is_online ? "is-online" : ""}`}>
                            {employee.is_online ? "Онлайн" : "Оффлайн"}
                        </span>
                        <div className="employee-actions">
                            {employee.is_superadmin && <ShieldCheck size={15} title="Суперадмин" />}
                            {!employee.is_self && (
                                <button
                                    type="button"
                                    title="Открыть звонилку"
                                    onClick={() => navigate("/dashboard")}
                                >
                                    <PhoneCall size={15} />
                                </button>
                            )}
                        </div>
                    </article>
                ))}
                {!isLoading && sortedEmployees.length === 0 && (
                    <p className="empty-copy">Сотрудники пока не найдены.</p>
                )}
            </div>
        </aside>
    );
};

export default EmployeeRightPanel;
